/**
 * Componente ImagePreview
 *
 * O que faz:
 * - Exibe um preview de imagem com estado de "enviando" (loading) enquanto o backend/n8n processa.
 * - Quando a URL estiver disponível, mostra a imagem clicável para abrir em tela cheia (via callback do pai).
 *
 * Onde é usado:
 * - Utilizado no ChatWindow para renderizar mensagens do tipo imagem ("imagem").
 *
 * Como se conecta:
 * - Recebe imageUrl (string opcional), fileName (string) e isLoading (boolean) para controlar o estado visual.
 * - onOpen é uma função opcional chamada ao clicar na imagem já disponível.
 */
import React from 'react';
import { Loader2, Image as ImageIcon } from 'lucide-react';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

interface ImagePreviewProps {
  imageUrl?: string;
  fileName: string;
  isLoading?: boolean;
  onOpen?: (sourceElement: HTMLImageElement) => void; // usado para abrir modal no pai (ChatWindow)
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({ imageUrl, fileName, isLoading = false, onOpen }) => {
  return (
    <div className="relative inline-block">
      {/* Overlay de carregamento enquanto processa */}
      {isLoading && (
        <div className="absolute inset-0 bg-muted/70 rounded-lg flex items-center justify-center z-10">
          <ThinkingOrbLoader state="working" size={20} />
          <span className="ml-2 text-sm text-muted-foreground">Enviando...</span>
        </div>
      )}

      {imageUrl ? (
        <img
          src={imageUrl}
          alt={fileName}
          loading="lazy"
          decoding="async"
          className="max-w-[70vw] sm:max-w-xs max-w-full max-h-72 w-auto h-auto rounded-lg object-contain cursor-pointer select-none"
          // Envia o elemento clicado para o pai, assim o modal pode animar a partir da posição real da miniatura.
          onClick={(event) => onOpen?.(event.currentTarget)}
        />
      ) : (
        // Fallback visual enquanto não temos URL (ex.: só nome do arquivo)
        <div className="bg-muted p-4 rounded-lg flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{fileName}</span>
        </div>
      )}
    </div>
  );
};

export default ImagePreview;
